<template>
  <Box class="aula-card" space="medium">
    <div class="card-header">
      <div class="status-badge">
        <Badge
          :text="getStatusText(aula.aulaPraticaStatus)"
          :color="getStatusColor(aula.aulaPraticaStatus)"
        />
      </div>
      <div class="card-actions">
        <IconButton
          v-if="canSendAula"
          @handle:click="$emit('sendClassroom')"
          color="primary"
          title="Enviar Aula"
          :disabled="loading"
        >
          <Send :size="18" />
        </IconButton>

        <IconButton
          @handle:click="$emit('abrirModificacaoAula')"
          color="secondary"
          title="Modificar aula"
          :disabled="loading"
        >
          <Wrench :size="18" />
        </IconButton>

        <IconButton
          @handle:click="$emit('abrirAulasDoInstrutor')"
          color="secondary"
          title="Visualizar aulas do instrutor"
          :disabled="loading"
        >
          <FormatListBulleted :size="18" />
        </IconButton>

        <IconButton
          @handle:click="$emit('abrirAulasDoAluno')"
          color="secondary"
          title="Visualizar aulas do aluno"
          :disabled="loading"
        >
          <Eye :size="18" />
        </IconButton>

        <IconButton
          @handle:click="confirmRemoveAnnotation"
          color="danger"
          title="Remover Anotação"
          :disabled="loading"
        >
          <Close :size="18" />
        </IconButton>
      </div>
    </div>

    <div class="card-content">
      <div class="student-info">
        <div class="info-group">
          <label>Aluno:</label>
          <span class="info-value">{{ aula.alunoNome }}</span>
        </div>
        <div class="info-group">
          <label>CPF:</label>
          <span class="info-value">{{ formatCPF(aula.alunoCPF) }}</span>
        </div>
        <div class="info-group">
          <label>Renach:</label>
          <span class="info-value">{{ aula.renach || '-' }}</span>
        </div>
      </div>

      <div class="class-info">
        <div class="info-group">
          <label>Instrutor:</label>
          <span class="info-value">{{ aula.instrutorNome }}</span>
        </div>
        <div class="info-group">
          <label>Data:</label>
          <span class="info-value">{{ formatDate(aula.aulaPraticaData) }}</span>
        </div>
        <div class="info-group">
          <label>Categoria:</label>
          <span class="info-value">{{ aula.categoria || '-' }}</span>
        </div>
      </div>

      <div class="additional-info" v-if="aula.observacoes">
        <div class="info-group full-width">
          <label>Observações:</label>
          <span class="info-value">{{ aula.observacoes }}</span>
        </div>
      </div>
    </div>

    <div v-if="loading" class="loading-overlay">
      <LoadingSpinner />
    </div>
  </Box>
</template>

<script>
import Box from 'core/Box'
import Badge from 'core/Badge'
import IconButton from 'core/IconButton'
import LoadingSpinner from 'core/LoadingSpinner'

import Send from 'icons/Send'
import Wrench from 'icons/Wrench'
import FormatListBulleted from 'icons/FormatListBulleted'
import Eye from 'icons/Eye'
import Close from 'icons/Close'

import utils from '@/services/utils.js'

export default {
  name: 'ModernCardAula',
  components: {
    Box,
    Badge,
    IconButton,
    LoadingSpinner,
    Send,
    Wrench,
    FormatListBulleted,
    Eye,
    Close,
  },
  props: {
    aula: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      utils: utils,
      statusEnviados: ['Em análise', 'Aceito', 'Transação efetuada com sucesso']
    }
  },
  computed: {
    canSendAula () {
      const status = this.aula.aulaPraticaStatus
      return !this.statusEnviados.includes(status)
    }
  },
  methods: {
    getStatusText (status) {
      if (!status) return 'PENDENTE'
      return status.toUpperCase()
    },

    getStatusColor (status) {
      if (!status) return 'warning'

      switch (status.toLowerCase()) {
        case 'aceito':
          return 'success'
        default:
          return 'danger'
      }
    },

    formatCPF (cpf) {
      if (!cpf) return '-'
      return this.utils.formatarCPF(cpf)
    },

    formatDate (date) {
      if (!date) return '-'
      return this.utils.formatDate(date)
    },

    confirmRemoveAnnotation () {
      if (confirm('Deseja mesmo remover a anotação? A operação é irreversível.')) {
        this.$emit('removerAnotacao')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.aula-card {
  position: relative;
  transition: all 0.2s ease;
  border: 1px solid #e9ecef;

  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    border-color: #dee2e6;
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1.5rem;

    .status-badge {
      flex-shrink: 0;
    }

    .card-actions {
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;

      @media (max-width: 576px) {
        gap: 0.25rem;
      }
    }
  }

  .card-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 1rem;
    }

    .student-info,
    .class-info {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .additional-info {
      grid-column: 1 / -1;
      margin-top: 1rem;
      padding-top: 1rem;
      border-top: 1px solid #e9ecef;
    }

    .info-group {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;

      &.full-width {
        grid-column: 1 / -1;
      }

      label {
        font-size: 0.75rem;
        font-weight: 600;
        color: #6c757d;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .info-value {
        font-size: 0.875rem;
        font-weight: 500;
        color: #202224;
        word-break: break-word;
      }
    }
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    z-index: 10;
  }
}

// Responsividade para telas muito pequenas
@media (max-width: 480px) {
  .aula-card {
    .card-header {
      flex-direction: column;
      gap: 1rem;
      align-items: stretch;

      .card-actions {
        justify-content: center;
      }
    }

    .card-content {
      .info-group {
        label {
          font-size: 0.7rem;
        }

        .info-value {
          font-size: 0.8rem;
        }
      }
    }
  }
}
</style>
